import React from 'react';
import { Box, Typography, Paper, Button, Chip, Skeleton, CircularProgress } from '@leap4/frontend';
import { DynamicDatagrid, MuiDataGrid, MuiIcons } from '@leap4/frontend';
import * as XLSX from 'xlsx';
import { IReportDataRow, IReportTotals } from '../report.interfaces';
import { REPORT_METRICS, REPORT_DIMENSIONS } from '../report.constants';

const { Download } = MuiIcons;

type GridColDef = MuiDataGrid.GridColDef;
type GridRenderCellParams = MuiDataGrid.GridRenderCellParams;

interface ReportResultsTableProps {
  data: IReportDataRow[];
  totals: IReportTotals;
  selectedMetrics: string[];
  selectedDimensions: string[];
  aggregationPeriod: 'daily' | 'monthly' | 'sum';
  isLoading: boolean;
  filterCount: number;
  constraintCount: number;
}

const formatNumber = (n: number | undefined): string => {
  if (n === undefined || n === null) return '-';
  return n.toLocaleString('en-US');
};

const formatCurrency = (n: number | undefined): string => {
  if (n === undefined || n === null) return '-';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (n: number | undefined): string => {
  if (n === undefined || n === null) return '-';
  return `${n.toFixed(2)}%`;
};

const getMetricFormatter = (metricKey: string): ((n: number | undefined) => string) => {
  if (['revenue', 'cost', 'profit', 'netRevenue', 'netCost', 'netProfit', 'serviceCost', 'cpm', 'rpm'].includes(metricKey)) {
    return formatCurrency;
  }
  if (['fillRate', 'adFillRate', 'vcr', 'profitPercent'].includes(metricKey)) {
    return formatPercent;
  }
  return formatNumber;
};

interface FlattenedRow {
  id: number;
  date?: string;
  supplyTag?: string;
  supplyPartner?: string;
  supplyPartnerAlias?: string;
  demandTag?: string;
  demandPartner?: string;
  demandPartnerAlias?: string;
  adServer?: string;
  dataPoint?: string;
  revenue?: number;
  cost?: number;
  impressions?: number;
  requests?: number;
  profit?: number;
  fillRate?: number;
  adFillRate?: number;
  vcr?: number;
  cpm?: number;
  rpm?: number;
  profitPercent?: number;
  netRevenue?: number;
  netCost?: number;
  netProfit?: number;
  serviceCost?: number;
  completion?: number;
  incomingRequests?: number;
}

export const ReportResultsTable: React.FC<ReportResultsTableProps> = ({
  data,
  totals,
  selectedMetrics,
  selectedDimensions,
  aggregationPeriod,
  isLoading,
  filterCount,
  constraintCount,
}) => {
  const columns: GridColDef[] = [];

  // Auto-add date column based on aggregation period
  if (aggregationPeriod === 'daily' || aggregationPeriod === 'monthly') {
    columns.push({
      field: 'date',
      headerName: aggregationPeriod === 'daily' ? 'Date' : 'Month',
      width: 120,
    });
  }

  selectedDimensions.forEach((dimKey) => {
    const dimConfig = REPORT_DIMENSIONS.find((d) => d.key === dimKey);
    if (!dimConfig) return;

    columns.push({
      field: dimKey,
      headerName: dimConfig.label,
      width: 180,
    });
  });

  selectedMetrics.forEach((metricKey) => {
    const metricConfig = REPORT_METRICS.find((m) => m.key === metricKey);
    if (!metricConfig) return;

    const formatter = getMetricFormatter(metricKey);

    columns.push({
      field: metricKey,
      headerName: metricConfig.label,
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            fontVariantNumeric: 'tabular-nums',
            color: ['revenue', 'profit', 'netRevenue', 'netProfit'].includes(metricKey)
              ? 'success.main'
              : 'text.primary',
            fontWeight: ['revenue', 'profit'].includes(metricKey) ? 500 : 400,
          }}
        >
          {formatter(params.value as number | undefined)}
        </Typography>
      ),
    });
  });

  const rows: FlattenedRow[] = data.map((row, index) => {
    const flatRow: FlattenedRow = { id: index };

    if (row.dimensions) {
      if (row.dimensions.timestamp) flatRow.date = row.dimensions.timestamp;
      if (row.dimensions.supplyTag) flatRow.supplyTag = row.dimensions.supplyTag.name || row.dimensions.supplyTag.id;
      if (row.dimensions.supplyPartner) flatRow.supplyPartner = row.dimensions.supplyPartner.name || row.dimensions.supplyPartner.id;
      if (row.dimensions.supplyPartnerAlias) flatRow.supplyPartnerAlias = row.dimensions.supplyPartnerAlias.name || row.dimensions.supplyPartnerAlias.id;
      if (row.dimensions.demandTag) flatRow.demandTag = row.dimensions.demandTag.name || row.dimensions.demandTag.id;
      if (row.dimensions.demandPartner) flatRow.demandPartner = row.dimensions.demandPartner.name || row.dimensions.demandPartner.id;
      if (row.dimensions.demandPartnerAlias) flatRow.demandPartnerAlias = row.dimensions.demandPartnerAlias.name || row.dimensions.demandPartnerAlias.id;
      if (row.dimensions.adServer) flatRow.adServer = row.dimensions.adServer.name || row.dimensions.adServer.id;
      if (row.dimensions.dataPoint) flatRow.dataPoint = row.dimensions.dataPoint.name || row.dimensions.dataPoint.id;
    }

    if (row.metrics) {
      Object.assign(flatRow, row.metrics);
    }

    return flatRow;
  });

  const totalsSummary = [
    { key: 'cost', label: 'Total Cost', color: '#1565c0' },
    { key: 'impressions', label: 'Total Impressions', color: '#7b1fa2' },
    { key: 'revenue', label: 'Total Revenue', color: '#2e7d32' },
    { key: 'profit', label: 'Total Profit', color: '#e65100' },
  ].filter((t) => selectedMetrics.includes(t.key));

  const handleExportToExcel = () => {
    if (rows.length === 0) return;

    const exportData = rows.map((row) => {
      const exportRow: Record<string, string | number | undefined> = {};

      // Add date column if applicable
      if (aggregationPeriod === 'daily' || aggregationPeriod === 'monthly') {
        exportRow[aggregationPeriod === 'daily' ? 'Date' : 'Month'] = row.date;
      }

      // Add dimension columns
      selectedDimensions.forEach((dimKey) => {
        const dimConfig = REPORT_DIMENSIONS.find((d) => d.key === dimKey);
        if (dimConfig) {
          exportRow[dimConfig.label] = row[dimKey as keyof FlattenedRow] as string;
        }
      });

      // Add metric columns with raw values (no formatting for Excel)
      selectedMetrics.forEach((metricKey) => {
        const metricConfig = REPORT_METRICS.find((m) => m.key === metricKey);
        if (metricConfig) {
          exportRow[metricConfig.label] = row[metricKey as keyof FlattenedRow] as number;
        }
      });

      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

    // Generate filename with current date
    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `report_${dateStr}.xlsx`);
  };

  if (isLoading) {
    return (
      <Paper
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            gap: 3,
            py: 8,
          }}
        >
          <CircularProgress size={56} thickness={4} sx={{ color: 'primary.main' }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
              Generating Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we process your data...
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Results
          </Typography>
          <Chip label={`${data.length} rows`} size="small" sx={{ bgcolor: 'grey.100' }} />
          {filterCount > 0 && (
            <Chip
              label={`${filterCount} filter${filterCount !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
            />
          )}
          {constraintCount > 0 && (
            <Chip
              label={`${constraintCount} constraint${constraintCount !== 1 ? 's' : ''}`}
              size="small"
              color="warning"
            />
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<Download />}
          size="small"
          onClick={handleExportToExcel}
          disabled={data.length === 0}
        >
          Export to Excel
        </Button>
      </Box>

      {totalsSummary.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${totalsSummary.length}, 1fr)`,
            gap: '1px',
            bgcolor: 'divider',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {totalsSummary.map((item) => {
            const formatter = getMetricFormatter(item.key);
            const value = totals[item.key as keyof IReportTotals];
            return (
              <Box key={item.key} sx={{ bgcolor: 'background.paper', p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {item.label}
                </Typography>
                {isLoading ? (
                  <Skeleton width={100} height={28} />
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 700, color: item.color }}>
                    {formatter(value)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ width: '100%' }}>
        <DynamicDatagrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          loadError={false}
          pageSize={25}
          refreshFunc={() => {}}
          style={{ minHeight: '400px', width: '100%' }}
        />
      </Box>
    </Paper>
  );
};
